// apps/web/src/components/users/device-expansion.tsx — Fase 01, Plano 62 (USR-03).
// Revogação administrativa via apiMutate (cookie + x-csrf-token). Erro fica
// na expansão — toast sumiria antes de o operador associar à linha (UI-SPEC).
import { useState, type ReactElement } from 'react';
import { apiMutate } from '../../lib/csrf-fetch';
import { rotuloStatus } from '../../lib/rotulos';

export interface DeviceItem {
  readonly id: string;
  readonly label: string;
  readonly scope: string;
  readonly status: 'ACTIVE' | 'REVOKED' | 'EXPIRED';
  readonly expiresAt: string;
  readonly revokedAt?: string | null;
}

export interface DeviceExpansionProps {
  readonly userId: string;
  readonly userName: string;
  readonly devices: readonly DeviceItem[];
  readonly onDeviceRevoked?: (deviceId: string) => void;
}

const REVOKE_ERROR = 'Não foi possível revogar o dispositivo.';

export function DeviceExpansion({ userName, devices, onDeviceRevoked }: DeviceExpansionProps): ReactElement {
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryDeviceId, setRetryDeviceId] = useState<string | null>(null);

  const handleRevoke = async (deviceId: string) => {
    setRevokingId(deviceId);
    setRetryDeviceId(deviceId);
    setError(null);
    try {
      const res = await apiMutate(`/api/v1/devices/${deviceId}/revoke`, { method: 'POST' });
      if (!res.ok) {
        throw new Error(REVOKE_ERROR);
      }
      setRetryDeviceId(null);
      onDeviceRevoked?.(deviceId);
    } catch {
      setError(REVOKE_ERROR);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div className="device-expansion p-4 bg-slate-50 border-t border-slate-200">
      <h4 className="text-sm font-semibold text-slate-700 mb-2">Dispositivos Conectados: {userName}</h4>
      {error && (
        <div className="text-xs text-red-600 mb-2" role="alert">
          <p>{error}</p>
          {retryDeviceId && (
            <button
              type="button"
              onClick={() => void handleRevoke(retryDeviceId)}
              disabled={revokingId !== null}
              className="mt-1 font-medium underline"
            >
              Tentar novamente
            </button>
          )}
        </div>
      )}

      {devices.length === 0 ? (
        <div className="text-xs text-slate-500 py-2">Nenhum dispositivo registrado.</div>
      ) : (
        <div className="space-y-2">
          {devices.map((dev) => (
            <div key={dev.id} className="flex items-center justify-between p-2 bg-white rounded border border-slate-200 text-xs">
              <div>
                <span className="font-medium text-slate-900 mr-2">{dev.label}</span>
                <span className="text-slate-500 mr-2">[{dev.scope}]</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  dev.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {rotuloStatus(dev.status)}
                </span>
              </div>
              {dev.status === 'ACTIVE' && (
                <button
                  type="button"
                  onClick={() => void handleRevoke(dev.id)}
                  disabled={revokingId === dev.id}
                  className="px-2 py-1 text-red-600 border border-red-200 hover:bg-red-50 rounded font-medium disabled:opacity-50"
                  aria-label={`Revogar dispositivo ${dev.label}`}
                >
                  {revokingId === dev.id ? 'Revogando...' : 'Revogar'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
